import { createSelector } from '@reduxjs/toolkit';

import { compareId } from 'mastodon/compare_id';
import type { NotificationGroup } from 'mastodon/models/notification_group';
import type { NotificationGap } from 'mastodon/reducers/notification_groups';
import type { RootState } from 'mastodon/store';

import {
  selectSettingsNotificationsExcludedTypes,
  selectSettingsNotificationsQuickFilterActive,
  selectSettingsNotificationsQuickFilterShow,
} from './settings';

const filterNotificationsByAllowedTypes = (
  showFilterBar: boolean,

  allowedType: string,

  excludedTypes: string[],

  notifications: (NotificationGroup | NotificationGap)[],

  accounts: RootState['accounts'],

  statuses: RootState['statuses'],
) => {
  if (!showFilterBar || allowedType === 'all') {
    // Keep the existing behavior for the All tab.

    return notifications.filter(
      (item) => item.type === 'gap' || !excludedTypes.includes(item.type),
    );
  }

  if (
    allowedType === 'mention' ||
    allowedType === 'shop' ||
    allowedType === 'direct'
  ) {
    return notifications.filter((item) => {
      if (item.type === 'gap') return true;

      // Preserve Mastodon's existing Mentions behavior, including quote

      // notifications, then split it into normal/shop/direct tabs.

      if (item.type !== 'mention' && item.type !== 'quote') return false;

      if (!('statusId' in item) || !item.statusId) return false;

      const visibility = statuses.getIn([item.statusId, 'visibility']);

      const accountId = item.sampleAccountIds.at(0);

      const acct = accountId ? accounts.getIn([accountId, 'acct']) : undefined;

      const isDirect = visibility === 'direct';

      const isShop = acct === 'siren_shop';

      if (allowedType === 'direct') return isDirect;

      if (allowedType === 'shop') return !isDirect && isShop;

      // Normal mentions exclude both DMs and replies from the shop account.

      return !isDirect && !isShop;
    });
  }

  return notifications.filter(
    (item) =>
      item.type === 'gap' ||
      allowedType === item.type ||
      (allowedType === 'collection' &&
        (item.type === 'collection_update' ||
          item.type === 'added_to_collection')),
  );
};

export const selectNotificationGroups = createSelector(
  [
    selectSettingsNotificationsQuickFilterShow,

    selectSettingsNotificationsQuickFilterActive,

    selectSettingsNotificationsExcludedTypes,

    (state: RootState) => state.notificationGroups.groups,

    (state: RootState) => state.accounts,

    (state: RootState) => state.statuses,
  ],

  filterNotificationsByAllowedTypes,
);

const selectPendingNotificationGroups = createSelector(
  [
    selectSettingsNotificationsQuickFilterShow,

    selectSettingsNotificationsQuickFilterActive,

    selectSettingsNotificationsExcludedTypes,

    (state: RootState) => state.notificationGroups.pendingGroups,

    (state: RootState) => state.accounts,

    (state: RootState) => state.statuses,
  ],

  filterNotificationsByAllowedTypes,
);

export const selectUnreadNotificationGroupsCount = createSelector(
  [
    (s: RootState) => s.notificationGroups.lastReadId,
    selectNotificationGroups,
    selectPendingNotificationGroups,
  ],
  (notificationMarker, groups, pendingGroups) => {
    return (
      groups.filter(
        (group) =>
          group.type !== 'gap' &&
          group.page_max_id &&
          compareId(group.page_max_id, notificationMarker) > 0,
      ).length +
      pendingGroups.filter(
        (group) =>
          group.type !== 'gap' &&
          group.page_max_id &&
          compareId(group.page_max_id, notificationMarker) > 0,
      ).length
    );
  },
);

// Whether there is any unread notification according to the user-facing state
export const selectAnyPendingNotification = createSelector(
  [
    (s: RootState) => s.notificationGroups.readMarkerId,
    selectNotificationGroups,
  ],
  (notificationMarker, groups) => {
    return groups.some(
      (group) =>
        group.type !== 'gap' &&
        group.page_max_id &&
        compareId(group.page_max_id, notificationMarker) > 0,
    );
  },
);

export const selectPendingNotificationGroupsCount = createSelector(
  [selectPendingNotificationGroups],
  (pendingGroups) =>
    pendingGroups.filter((group) => group.type !== 'gap').length,
);
